import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Avatar,
  Grid,
  Chip,
  Button,
  Skeleton,
  Alert,
  Divider,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Event as EventIcon,
  People as PeopleIcon,
  CalendarToday as CalendarTodayIcon,
  LocationOn as LocationOnIcon,
} from '@mui/icons-material';
import apiClient from '../services/api';

interface Organizer {
  id: number;
  name: string;
  username: string | null;
  events_count: number;
  total_participants: number;
  member_since: string;
}

interface Event {
  id: number;
  title: string;
  start_datetime: string;
  city_name: string | null;
  price: number;
  max_participants: number;
  is_public: boolean;
  current_participants: number;
}

interface OrganizerProfileResponse {
  organizer: Organizer;
  events: Event[];
  active_events_count: number;
}

const OrganizerPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<OrganizerProfileResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadOrganizer = async () => {
      try {
        setLoading(true);
        const response = await apiClient.get(`/api/organizers/${id}`);
        setData(response.data);
        
        // Отслеживаем просмотр
        const urlParams = new URLSearchParams(window.location.search);
        await apiClient.post('/api/organizers/track', {
          organizer_user_id: Number(id),
          event_type: 'link_view',
          utm_source: urlParams.get('utm_source'),
          utm_medium: urlParams.get('utm_medium'),
          utm_campaign: urlParams.get('utm_campaign'),
        });
      } catch (err: any) {
        console.error('Ошибка загрузки организатора:', err);
        setError('Не удалось загрузить информацию об организаторе');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      loadOrganizer();
    }
  }, [id]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleJoinEvent = (eventId: number) => {
    navigate(`/events/${eventId}`);
  };

  if (loading) {
    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/events')} sx={{ mb: 2 }}>
          Назад
        </Button>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Skeleton variant="circular" width={100} height={100} sx={{ mb: 2 }} />
          <Skeleton variant="text" width="60%" sx={{ mb: 2 }} />
          <Skeleton variant="text" width="40%" />
        </Paper>
        <Grid container spacing={3}>
          {[1, 2, 3].map((i) => (
            <Grid item xs={12} md={4} key={i}>
              <Paper sx={{ p: 3 }}>
                <Skeleton variant="text" height={40} sx={{ mb: 2 }} />
                <Skeleton variant="text" />
                <Skeleton variant="text" />
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/events')} sx={{ mb: 2 }}>
          Назад
        </Button>
        <Alert severity="error">{error || 'Организатор не найден'}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Навигация */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/events')}>
          Назад к мероприятиям
        </Button>
      </Box>

      {/* Информация об организаторе */}
      <Paper sx={{ p: 4, mb: 3 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={8}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <Avatar
                sx={{
                  width: 100,
                  height: 100,
                  bgcolor: 'primary.main',
                  fontSize: '2.5rem',
                }}
              >
                {data.organizer.name[0]?.toUpperCase()}
              </Avatar>
              <Box>
                <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom>
                  {data.organizer.name}
                </Typography>
                {data.organizer.username && (
                  <Typography variant="body1" color="text.secondary" gutterBottom>
                    @{data.organizer.username}
                  </Typography>
                )}
                <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                  <Chip
                    icon={<EventIcon />}
                    label={`${data.organizer.events_count} мероприятий`}
                    color="primary"
                  />
                  <Chip
                    icon={<PeopleIcon />}
                    label={`${data.organizer.total_participants} участников`}
                    color="success"
                  />
                </Box>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ textAlign: { xs: 'left', md: 'right' } }}>
              <Typography variant="body2" color="text.secondary">
                <CalendarTodayIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                На сайте с {new Date(data.organizer.member_since).toLocaleDateString('ru-RU')}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Статистика */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="h3" fontWeight="bold" color="primary">
              {data.active_events_count}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Активных мероприятий
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="h3" fontWeight="bold" color="success.main">
              {data.organizer.total_participants}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Всего участников
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="h3" fontWeight="bold" color="info.main">
              {data.organizer.events_count}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Проведено событий
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Мероприятия */}
      <Typography variant="h5" gutterBottom fontWeight="bold" sx={{ mb: 2 }}>
        📅 Предстоящие мероприятия
      </Typography>

      {data.events.length === 0 ? (
        <Alert severity="info">
          В данный момент нет активных мероприятий. Загляните позже!
        </Alert>
      ) : (
        <Grid container spacing={3}>
          {data.events.map((event) => (
            <Grid item xs={12} md={4} key={event.id}>
              <Paper
                sx={{
                  p: 3,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  cursor: 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 6,
                  },
                }}
                onClick={() => handleJoinEvent(event.id)}
              >
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="h6" component="h3" gutterBottom noWrap>
                    {event.title}
                  </Typography>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <CalendarTodayIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(event.start_datetime)}
                    </Typography>
                  </Box>

                  {event.city_name && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <LocationOnIcon fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {event.city_name}
                      </Typography>
                    </Box>
                  )}

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <PeopleIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      {event.current_participants}/{event.max_participants} участников
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                    <Chip
                      label={event.is_public ? '🌐 Открытое' : '🔒 Закрытое'}
                      size="small"
                      variant="outlined"
                    />
                    {event.price > 0 && (
                      <Chip label={`${event.price} ₽`} size="small" color="primary" />
                    )}
                    {event.price === 0 && (
                      <Chip label="Бесплатно" size="small" color="success" />
                    )}
                  </Box>
                </Box>

                <Button
                  variant="contained"
                  fullWidth
                  onClick={(e) => {
                    e.stopPropagation();
                    handleJoinEvent(event.id);
                  }}
                >
                  Записаться
                </Button>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      <Divider sx={{ my: 4 }} />

      {/* Призыв к действию */}
      <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'primary.light' }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          Хотите проводить мероприятия с этим организатором?
        </Typography>
        <Typography variant="body1" sx={{ mb: 3 }}>
          Зарегистрируйтесь на платформе и записывайтесь на мероприятия
        </Typography>
        <Button
          variant="contained"
          size="large"
          onClick={() => navigate('/login')}
          sx={{ mr: 2 }}
        >
          Войти / Регистрация
        </Button>
      </Paper>
    </Box>
  );
};

export default OrganizerPage;
